import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ValidatorForm } from "components";
import CompanyService from "../../services/CompanyService";
import { isValidPhoneNumber } from "libphonenumber-js";
import {
  Button,
  Typography,
  Box,
  Link,
  Dialog,
  DialogContent,
  Fade,
  Stack,
  LinearProgress,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  RestaurantInfoStep,
  BusinessDetailsStep,
  CurrencyChargesStep,
  CompleteSetupStep,
} from "./components";

const MotionBox = motion(Box);

const BRAND = "#F9735B";
const BRAND_DARK = "#DC5F49";
const INK = "#0F172A";
const MUTED = "#475569";
const BORDER = "#D9E2EC";
const CLOUD = "#F8FAFC";

const CompanyRegistration = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    taxId: "",
    vatRate: "",
    serviceChargeRate: "",
    serviceChargeRateDining: "",
    serviceChargeRateTakeaway: "",
    serviceChargeRateDelivery: "",
    currency: "LKR",
    lat: "",
    lng: "",
    district: "",
    country: "",
    description: "",
    slogan: "",
    closeTime: "",
    facebook: "",
    instagram: "",
  });
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [successDialog, setSuccessDialog] = useState(false);

  const navigate = useNavigate();

  const steps = [
    {
      label: "Restaurant Info",
      title: "Tell us about your restaurant",
      description: "Basic account and restaurant details to get started.",
      icon: StorefrontIcon,
    },
    {
      label: "Business Details",
      title: "Add business profile details",
      description: "Set up your description, slogan and tax ID.",
      icon: BusinessOutlined,
    },
    {
      label: "Currency & Charges",
      title: "Configure currency and company charges",
      description: "Set VAT, currency and service charge rates.",
      icon: PaidOutlinedIcon,
    },
    {
      label: "Complete Setup",
      title: "Finish profile and branding",
      description: "Add social links and upload your logo.",
      icon: CheckCircleOutlineIcon,
    },
  ];

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone: value });

    if (value) {
      try {
        const phoneNumber = `+${value}`;
        if (isValidPhoneNumber(phoneNumber)) {
          setPhoneError("");
        } else {
          setPhoneError("Please enter a valid phone number");
        }
      } catch (error) {
        setPhoneError("Please enter a valid phone number");
      }
    } else {
      setPhoneError("Phone is required");
    }
  };

  const handleFileChange = (files) => {
    if (files && files.length > 0) {
      setLogo(files[0]);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();

      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        if (value !== null && value !== undefined && value !== "") {
          formDataToSend.append(key, value);
        }
      });

      if (logo) {
        formDataToSend.append("logo", logo);
      }

      await CompanyService.create(formDataToSend);
      setSuccessDialog(true);
    } catch (error) {
      console.error("Company Registration Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <RestaurantInfoStep
            formData={formData}
            handleChange={handleChange}
            handlePhoneChange={handlePhoneChange}
            phoneError={phoneError}
          />
        );
      case 1:
        return (
          <BusinessDetailsStep
            formData={formData}
            handleChange={handleChange}
          />
        );
      case 2:
        return (
          <CurrencyChargesStep
            formData={formData}
            handleChange={handleChange}
          />
        );
      case 3:
        return (
          <CompleteSetupStep
            formData={formData}
            handleChange={handleChange}
            logo={logo}
            handleFileChange={handleFileChange}
          />
        );
      default:
        return null;
    }
  };

  const progress = ((activeStep + 1) / steps.length) * 100;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Left Panel */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          width: "50%",
          flexShrink: 0,
          position: "sticky",
          top: 0,
          height: "100vh",
          flexDirection: "column",
          background:
            "radial-gradient(ellipse at 20% 0%, rgba(56,189,248,0.15), transparent 50%), radial-gradient(ellipse at 80% 100%, rgba(79,70,229,0.15), transparent 50%), linear-gradient(160deg, rgba(11,21,50,0.90) 0%, rgba(21,32,64,0.85) 50%, rgba(26,40,80,0.90) 100%), url('/assets/images/login_signup.webp')",
          backgroundSize: "cover, cover, cover, cover",
          backgroundPosition: "center, center, center, center",
          backgroundRepeat: "no-repeat",
          overflow: "hidden",
        }}
      >
        {/* All content centered */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: { md: 4, lg: 6 },
            py: 4,
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src="/assets/logo.png"
            alt="Menux"
            sx={{ height: 42, width: "auto", mb: 4 }}
          />

          <Typography
            sx={{
              fontFamily: '"exo", "Inter", sans-serif',
              color: "#F8FAFC",
              fontSize: { md: "1.85rem", lg: "2.3rem" },
              fontWeight: 700,
              lineHeight: 1.15,
              mb: 1.2,
              maxWidth: 400,
            }}
          >
            Set up your restaurant in minutes
          </Typography>
          <Typography
            sx={{
              color: "rgba(248,250,252,0.5)",
              fontFamily: '"Inter", sans-serif',
              fontSize: "0.88rem",
              lineHeight: 1.7,
              mb: 4,
              maxWidth: 340,
            }}
          >
            QR Menu, POS and Kitchen Display — all configured in one smooth flow.
          </Typography>

          {/* Step cards */}
          <Stack spacing={0.8} sx={{ width: "100%", maxWidth: 340 }}>
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.2,
                    borderRadius: "12px",
                    background: isActive
                      ? "rgba(249,115,91,0.1)"
                      : isCompleted
                      ? "rgba(52,211,153,0.05)"
                      : "rgba(248,250,252,0.03)",
                    border: isActive
                      ? "1px solid rgba(249,115,91,0.25)"
                      : "1px solid rgba(248,250,252,0.06)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      background: isActive
                        ? "rgba(249,115,91,0.2)"
                        : isCompleted
                        ? "rgba(52,211,153,0.15)"
                        : "rgba(248,250,252,0.05)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {isCompleted ? (
                      <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#34D399" }} />
                    ) : (
                      React.createElement(step.icon, {
                        style: {
                          fontSize: 15,
                          color: isActive ? BRAND : "rgba(248,250,252,0.25)",
                        },
                      })
                    )}
                  </Box>
                  <Typography
                    sx={{
                      flex: 1,
                      fontFamily: '"Inter", sans-serif',
                      fontSize: "0.82rem",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive
                        ? "#F8FAFC"
                        : isCompleted
                        ? "rgba(248,250,252,0.6)"
                        : "rgba(248,250,252,0.3)",
                      textAlign: "left",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {step.label}
                  </Typography>
                  {isCompleted && (
                    <CheckCircleOutlineIcon sx={{ fontSize: 15, color: "#34D399", flexShrink: 0 }} />
                  )}
                  {isActive && (
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: BRAND, boxShadow: `0 0 10px ${BRAND}`, flexShrink: 0 }} />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* Copyright */}
        <Box sx={{ pb: 2.5, textAlign: "center" }}>
          <Typography sx={{ fontFamily: '"Inter", sans-serif', color: "rgba(248,250,252,0.2)", fontSize: "0.72rem" }}>
            &copy; {new Date().getFullYear()} Menux. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* Right Panel */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          background: CLOUD,
          minHeight: "100vh",
          overflow: "auto",
        }}
      >
        {/* Mobile header */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            px: 2.5,
            pt: 2.5,
            pb: 2,
            background:
              "radial-gradient(circle at 15% 20%, rgba(56,189,248,0.18), transparent 45%), radial-gradient(circle at 88% 85%, rgba(79,70,229,0.18), transparent 45%), linear-gradient(160deg, rgba(11,21,50,0.90) 0%, rgba(24,39,69,0.85) 100%), url('/assets/images/login_signup.webp')",
            backgroundSize: "cover, cover, cover, cover",
            backgroundPosition: "center, center, center, center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <Box component="img" src="/assets/logo.png" alt="Menux" sx={{ height: 34, width: "auto", mb: 1.2 }} />
          <Typography sx={{ fontFamily: '"exo", "Inter", sans-serif', color: "#F8FAFC", fontSize: "1.4rem", fontWeight: 700, lineHeight: 1.15 }}>
            Restaurant Onboarding
          </Typography>
          <Typography sx={{ mt: 0.6, color: "rgba(248,250,252,0.72)", fontFamily: '"Inter", sans-serif', fontSize: "0.82rem", lineHeight: 1.6 }}>
            Set up your digital restaurant in minutes.
          </Typography>
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: 640,
            mx: "auto",
            px: { xs: 2.5, sm: 3.5, md: 4.5 },
            py: { xs: 3, md: 4 },
          }}
        >
          {/* Progress bar */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: "0.75rem", fontWeight: 600, color: MUTED }}>
                Step {activeStep + 1} of {steps.length}
              </Typography>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: "0.75rem", fontWeight: 600, color: BRAND }}>
                {Math.round(progress)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 5,
                borderRadius: 3,
                bgcolor: "rgba(15,23,42,0.06)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  bgcolor: BRAND,
                  transition: "transform 0.4s ease",
                },
              }}
            />
          </Box>

          {/* Step header */}
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                background: "rgba(249,115,91,0.1)",
                border: "1px solid rgba(249,115,91,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: BRAND,
              }}
            >
              {React.createElement(steps[activeStep].icon, { style: { fontSize: 19 } })}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: '"exo", "Inter", sans-serif',
                  fontSize: { xs: "1.1rem", md: "1.25rem" },
                  fontWeight: 700,
                  color: INK,
                  lineHeight: 1.2,
                }}
              >
                {steps[activeStep].title}
              </Typography>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: "0.8rem", color: MUTED, mt: 0.15 }}>
                {steps[activeStep].description}
              </Typography>
            </Box>
          </Stack>

          {/* Form */}
          <ValidatorForm
            onSubmit={activeStep === steps.length - 1 ? handleSubmit : handleNext}
          >
            <AnimatePresence mode="wait">
              <MotionBox
                key={activeStep}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
              >
                {renderStepContent(activeStep)}
              </MotionBox>
            </AnimatePresence>

            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.2, mt: 2.5 }}>
              {activeStep > 0 && (
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    py: 1.15,
                    px: 2.5,
                    borderRadius: "10px",
                    borderColor: "rgba(249,115,91,0.3)",
                    color: BRAND_DARK,
                    textTransform: "none",
                    fontWeight: 700,
                    fontFamily: '"exo", "Inter", sans-serif',
                    bgcolor: "#fff",
                    "&:hover": { borderColor: BRAND, background: "rgba(249,115,91,0.06)" },
                  }}
                >
                  Back
                </Button>
              )}

              {activeStep > 0 && activeStep < steps.length - 1 && (
                <Button
                  variant="outlined"
                  onClick={handleNext}
                  disabled={loading}
                  sx={{
                    py: 1.15,
                    px: 2.5,
                    borderRadius: "10px",
                    borderColor: BORDER,
                    color: MUTED,
                    textTransform: "none",
                    fontWeight: 700,
                    fontFamily: '"exo", "Inter", sans-serif',
                    bgcolor: "#fff",
                    "&:hover": { borderColor: "#B9C7D8", background: "#fff" },
                  }}
                >
                  Skip for now
                </Button>
              )}

              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                endIcon={activeStep === steps.length - 1 ? <CheckCircleOutlineIcon /> : <ArrowForwardIcon />}
                sx={{
                  flex: 1,
                  py: 1.15,
                  borderRadius: "10px",
                  background: BRAND,
                  textTransform: "none",
                  fontWeight: 700,
                  fontFamily: '"exo", "Inter", sans-serif',
                  boxShadow: "0 8px 20px rgba(249,115,91,0.22)",
                  "&:hover": { background: BRAND_DARK, boxShadow: "0 10px 24px rgba(249,115,91,0.3)" },
                }}
              >
                {loading ? "Processing..." : activeStep === steps.length - 1 ? "Complete Registration" : "Continue"}
              </Button>
            </Box>

            <Typography sx={{ textAlign: "center", mt: 2.5, fontFamily: '"Inter", sans-serif', color: MUTED, fontSize: "0.84rem" }}>
              Already have an account?{" "}
              <Link
                href="/login"
                sx={{
                  color: BRAND_DARK,
                  fontWeight: 700,
                  textDecoration: "none",
                  borderBottom: "2px solid rgba(220,95,73,0.35)",
                  "&:hover": { borderBottomColor: BRAND_DARK },
                }}
              >
                Sign in here
              </Link>
            </Typography>
          </ValidatorForm>
        </Box>
      </Box>

      {/* Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={() => setSuccessDialog(false)}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            width: "min(560px, 92vw)",
            borderRadius: "18px",
            border: `1px solid ${BORDER}`,
            boxShadow: "0 28px 60px rgba(2,6,23,0.28)",
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.2, md: 3 }, textAlign: "center", background: "#fff" }}>
          <Box component="img" src="/assets/logo.png" alt="Menux" sx={{ height: 28, width: "auto", mb: 1.6 }} />

          <Box sx={{ display: "inline-flex", p: 1.6, borderRadius: "50%", bgcolor: "rgba(249,115,91,0.13)", mb: 1.2 }}>
            <RocketLaunchIcon sx={{ fontSize: 42, color: BRAND }} />
          </Box>

          <Typography sx={{ fontFamily: '"exo", "Inter", sans-serif', fontWeight: 700, fontSize: { xs: "1.4rem", md: "1.7rem" }, color: INK, mb: 0.8 }}>
            Registration Completed
          </Typography>

          <Typography sx={{ fontFamily: '"Inter", sans-serif', color: MUTED, fontSize: "0.9rem", lineHeight: 1.7, mb: 2 }}>
            Your restaurant profile was created successfully. Check your email for password reset instructions, then sign in to continue setup.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate("/login")}
            sx={{
              py: 1.2,
              borderRadius: "11px",
              background: BRAND,
              textTransform: "none",
              fontWeight: 700,
              fontFamily: '"exo", "Inter", sans-serif',
              "&:hover": { background: BRAND_DARK },
            }}
          >
            Go to Login
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CompanyRegistration;
